// Dagens vagt: briefen før start og gitteret med Del-knappen efter. Ingen server: teksten deles med
// telefonens deleark, eller kopieres, hvis browseren ikke har et.

import type { Par } from '../../ai/par';
import { vsPar } from '../../ai/par';
import { DAILY_MODS, dailyShareText, starText, yearSquare } from '../../content/daily';
import type { DailyPlan } from '../../content/daily';
import { DIFFICULTIES } from '../../sim';
import type { LevelDef } from '../../sim/types';
import { dailyRecord } from '../../storage';
import { h } from '../dom';
import { ICON } from '../icons';

const MOD_ICON = { tight: ICON.coin, stormy: ICON.wave, still: ICON.moon } as const;

/** Dagens par som tekst. */
export function parText(par: Par): string {
  return par.won ? `${starText(par.stars)} og ${par.green} % grønt` : 'en tabt vagt';
}

/** Briefens linje om parret. */
export function parBrief(par: Par): string {
  return par.won ? `Dagens par: ${parText(par)}. Det får en almindelig vagt.` : 'Dagens par: tabt. En almindelig vagt klarer den ikke.';
}

/** Linjen om stimen: hvor mange vagter i træk, og hvad i dag gør ved den. */
export function streakText(n: number, playedToday: boolean): string {
  if (n <= 0) return 'Spil i dag, og start en stime af vagter i træk.';
  const v = n === 1 ? 'vagt' : 'vagter';
  return playedToday ? `${n} ${v} i træk. Kom igen i morgen.` : `${n} ${v} i træk. Spil i dag, så bliver det ${n + 1}.`;
}

/** Spillerens resultat mod parret, kort og venligt. */
export function vsParText(you: Par, par: Par): string {
  const v = vsPar(you, par);
  if (v === 'over') return `Over dagens par: ${parText(par)}. Flot vagt.`;
  if (v === 'lige') return `Lige på dagens par: ${parText(par)}.`;
  return `Under dagens par: ${parText(par)}. Prøv igen?`;
}

/** Fyld en linje med parret, når det er regnet ud. Står der stadig, når det lander. */
function fillPar(el: HTMLElement, par: Promise<Par> | undefined, text: (p: Par) => string): void {
  if (!par) {
    el.hidden = true;
    return;
  }
  el.textContent = 'Regner dagens par ud…';
  el.classList.add('pending');
  par.then(
    (p) => {
      el.textContent = text(p);
      el.classList.remove('pending');
    },
    () => {
      el.hidden = true;
    },
  );
}

export function dailyBrief(
  plan: DailyPlan,
  level: LevelDef,
  handlers: { go(): void; back(): void },
  extra: { par?: Promise<Par>; streak?: number; playedToday?: boolean } = {},
): HTMLElement {
  const mod = DAILY_MODS[plan.mod];
  const best = dailyRecord(plan.key);
  const parLine = h('span', { 'aria-live': 'polite' });
  fillPar(parLine, extra.par, parBrief);
  return h(
    'div',
    { class: 'card levelstart daily' },
    h('p', { class: 'eyebrow' }, `Dagens vagt #${plan.number} · ${DIFFICULTIES[plan.difficulty].name}`),
    h('h1', { class: 'title small' }, level.title.toUpperCase()),
    h('p', { class: 'lead' }, 'Samme bane, samme vejr og samme regel for alle i dag. Spil den, og del dit gitter.'),
    // Regel, bedste, par og stime i én kolonne: på siden (844x390) står de til højre over Start.
    h(
      'div',
      { class: 'hows daily-hows' },
      h('div', { class: 'daily-mod' }, h('span', { class: 'how-icon', html: MOD_ICON[plan.mod], 'aria-hidden': 'true' }), h('span', {}, h('b', {}, mod.name), ' ', mod.text)),
      h(
        'p',
        { class: 'how' },
        h('span', { class: 'how-icon', html: ICON.star }),
        best ? `Dit bedste i dag: ${best.won ? `${best.stars} ${best.stars === 1 ? 'stjerne' : 'stjerner'}` : 'tabt'}. Prøv igen, hvis du vil.` : 'Et felt pr. år: grønt uden mørke byer, gult med én, rødt med flere.',
      ),
      extra.par ? h('p', { class: 'how slim daily-par-how' }, h('span', { class: 'how-icon', html: ICON.target, 'aria-hidden': 'true' }), parLine) : null,
      h('p', { class: 'how slim daily-streak' }, h('span', { class: 'how-icon', html: ICON.clock, 'aria-hidden': 'true' }), streakText(extra.streak ?? 0, !!extra.playedToday)),
    ),
    h(
      'div',
      { class: 'actions' },
      h('button', { type: 'button', class: 'btn ghost', onclick: () => handlers.back() }, 'Tilbage'),
      h('button', { type: 'button', class: 'btn primary', autofocus: true, onclick: () => handlers.go() }, h('span', { html: ICON.play }), 'Start'),
    ),
  );
}

/** Gitteret efter Dagens vagt og en knap, der deler det som tekst. */
export function dailyShare(
  plan: DailyPlan,
  level: LevelDef,
  marks: number[],
  stars: number,
  url: string,
  extra: { par?: Promise<Par>; you?: Par; streak?: number } = {},
): HTMLElement {
  let parNow: Par | null = null;
  const textNow = () => dailyShareText(plan, `Bane ${level.number}: ${level.title}`, level.years, marks, stars, url, { par: parNow, streak: extra.streak });
  let text = textNow();
  const parLine = h('p', { class: 'daily-par', 'aria-live': 'polite' });
  const you = extra.you;
  fillPar(parLine, you ? extra.par : undefined, (p) => {
    parNow = p;
    text = textNow();
    pre.textContent = text;
    return you ? vsParText(you, p) : '';
  });
  const streakLine = (extra.streak ?? 0) > 1 ? h('p', { class: 'daily-streak-n' }, `${extra.streak} vagter i træk`) : null;
  const grid = Array.from({ length: level.years }, (_, i) => yearSquare(marks[i] ?? null)).join('');
  const status = h('span', { class: 'daily-status', role: 'status' });
  const btn = h(
    'button',
    {
      type: 'button',
      class: 'btn secondary',
      onclick: async () => {
        status.textContent = '';
        try {
          if (typeof navigator.share === 'function') {
            await navigator.share({ text });
            return;
          }
        } catch (e) {
          // Annulleret af spilleren: ingen besked. Ellers falder vi tilbage til at kopiere.
          if (e instanceof DOMException && e.name === 'AbortError') return;
        }
        try {
          await navigator.clipboard.writeText(text);
          status.textContent = 'Kopieret. Sæt det ind, hvor du vil.';
        } catch {
          status.textContent = 'Kunne ikke kopiere. Marker teksten og kopier den selv.';
          pre.hidden = false;
        }
      },
    },
    h('span', { html: ICON.next }),
    'Del…',
  );
  const pre = h('pre', { class: 'daily-text-out', hidden: true }, text);
  return h(
    'section',
    { class: 'daily-result', 'aria-label': `Dagens vagt #${plan.number}` },
    h('p', { class: 'eyebrow' }, `Dagens vagt #${plan.number} · ${DAILY_MODS[plan.mod].name}`),
    h('p', { class: 'daily-grid', 'aria-label': `${marks.filter((m) => m === 0).length} af ${level.years} år uden mørke byer` }, grid),
    parLine,
    streakLine,
    btn,
    status,
    pre,
  );
}
